import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { QuotePricingPageDocument, QuotesTabsVisibilityDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import QuotePhasesTable from 'modules/finance/components/Quotes/QuotePhasesTable'
import { ConvertToProjectModal } from 'modules/finance/components/Quotes/modals/ConvertToProjectModal'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { onHeaderActionClick } from 'modules/finance/lib/quotesUtils'
import QuotePricingFinancialSummary from 'modules/finance/pages/QuotePricingPage/_QuotePricingFinancialSummary'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const QuotePricingPage = () => {
  const { quoteId } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showConvertToProjectModal, setShowConvertToProjectModal] = useState(false)
  const {
    access: { locale },
    company: { id: companyId },
  } = useCurrentSession()
  const downloadQuotePdf = useDownloadQuotePdf()
  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice({
    refetchQueries: [QuotesTabsVisibilityDocument],
  })

  const navigateToProject = () => {
    quote?.projectId &&
      navigate(
        navigation.projects.id.details.overview.fullPath({
          id: String(quote.projectId),
        })
      )
  }
  const navigateToInvoice = () => {
    quoteFinancialDocumentId &&
      navigate(
        navigation.finance.sales.salesInvoices.detail.fullPath({
          invoiceId: Number(quoteFinancialDocumentId),
        })
      )
  }
  const { data, error, loading } = useQuery(QuotePricingPageDocument, {
    variables: {
      id: Number(quoteId),
      companyId: Number(companyId),
    },
  })
  const authorized = !!data?.finance?.quote?._policies?.manage?.can
  const quote = data?.finance?.quote
  const quoteFinancialDocumentId = quote?.financialDocumentAssociation?.financialDocument?.id
  const lineItems = quote?.financeLineItems || []
  const isProductQuote = quote?.quoteType == 'products'

  const { has_legacy_job_catalog } = checkFeatures(
    ['legacy_job_catalog'],
    data?.features.featuresConnection.nodes
  )

  const isJobCatalogTreeEnabled = !has_legacy_job_catalog

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={authorized}
      loading={loading}
      error={error}
      onActionClick={(key) =>
        onHeaderActionClick(
          key,
          Number(quoteId),
          () => setShowConvertToProjectModal(true),
          convertQuoteIntoInvoice,
          downloadQuotePdf,
          navigateToProject,
          navigateToInvoice
        )
      }
    >
      {/* TODO: change Layout */}
      <TwoColumnLayout
        sideContent={
          quote &&
          quote.currency && (
            <QuotePricingFinancialSummary
              totalHours={quote.totalHours}
              baseCostCents={quote.baseCostCents}
              baseAmountCents={quote.baseAmountCents}
              taxesTotalAmountCents={quote.taxesTotalAmountCents}
              totalAmountCents={quote.totalAmountCents}
              currency={quote.currency}
              locale={locale}
              isProductQuote={isProductQuote}
            />
          )
        }
      >
        {quote && quote.currency && (
          <QuotePhasesTable
            currency={quote.currency}
            lineItems={lineItems}
            isProductQuote={isProductQuote}
            isDraftStatus={quote.status === 'draft'}
            isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
            legalEntityCountry={quote.legalEntity?.country}
            quoteId={quote.id}
          />
        )}
      </TwoColumnLayout>
      {showConvertToProjectModal && quote && quote.currency && quote.name && (
        <ConvertToProjectModal
          isOpen={showConvertToProjectModal}
          onClose={() => setShowConvertToProjectModal(false)}
          currency={quote.currency}
          quoteId={quote.id}
          defaultProjectName={quote.name}
          refetchQueries={[QuotesTabsVisibilityDocument]}
          totalAmountCents={quote.totalAmountCents}
          baseCostCents={quote.baseCostCents}
          totalHours={quote.totalHours}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default QuotePricingPage
